/**
 * FILE-OVERVIEW:
 * - 役割: フロントエンドの運用ログを S3 と CloudWatch Logs（/memo-app/web）へ保存する。
 * - 主な連携: index.js, App.js, services/calendarApi.js
 * - 本番運用上の注意: 機微情報（password/token等）は details に渡さないこと。
 */

import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { CloudWatchLogsClient, CreateLogStreamCommand, PutLogEventsCommand } from '@aws-sdk/client-cloudwatch-logs';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { AwsCredentialIdentity } from '@aws-sdk/types';
import awsExports from '../aws-exports';
import { asErrorLike } from '../utils/errorLike';

/** ログの重要度 */
export type LogLevel = 'INFO' | 'WARN' | 'ERROR';

/** ログの details。オブジェクト以外もそのまま素通しする */
export type LogDetails = Record<string, unknown> | unknown[] | string | number | boolean | null | undefined;

/** ログを出した利用者。認証セッションが取れないときは全て空になる */
export interface LogActor {
  userId: string | null;
  username: string | null;
  groups: string[];
}

/** S3へ書き出す1行分の内容 */
export interface AppLogPayload {
  timestamp: string;
  app: string;
  env: string;
  level: LogLevel;
  event: string;
  message: string | null;
  actor: LogActor;
  details: LogDetails;
  userAgent: string | null;
  path: string | null;
}

/** writeAppLog の引数 */
export interface WriteAppLogParams {
  level?: LogLevel;
  event: string;
  message?: string | null;
  details?: LogDetails;
}

const LOG_BUCKET_NAME = process.env.REACT_APP_S3_LOG_BUCKET_NAME || 'schedule-memo-log';
const REGION = process.env.REACT_APP_S3_LOG_REGION || process.env.REACT_APP_AWS_REGION || awsExports.aws_project_region || 'ap-northeast-1';
const APP_NAME = 'memo-app-web';
// 実行環境の識別子。本番/開発/ローカルのログを見分けるために全ログへ埋め込む
const APP_ENV = process.env.REACT_APP_ENV || 'local';
const LOG_FORMAT = (process.env.REACT_APP_APP_LOG_FORMAT || 'line').toLowerCase();
// CloudWatch Logs 側の出力先。S3の日次ファイルとは独立して両方に書き込む
// （2026-08-18追加。8/24のマージで消えていたため2026-09-15に復元）
const CW_LOG_GROUP_NAME = process.env.REACT_APP_CW_LOG_GROUP || '/memo-app/web';
const CW_ENABLED = String(process.env.REACT_APP_CW_LOG_ENABLED ?? 'true').toLowerCase() !== 'false';

const EVENT_LABELS: Record<string, string> = {
  auth_startup_check: '認証初期化チェック成功',
  auth_startup_check_failed: '認証初期化チェック失敗',
  auth_module_bootstrap_failed: '認証モジュール起動失敗',
  auth_session_resolved: '認証セッション取得成功',
  auth_session_resolve_failed: '認証セッション取得失敗',
  api_request_failed: 'APIリクエスト失敗',
};

const createDailyLogKey = (ext = 'log'): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');

  // 本番は既存の運用を壊さないようルート直下のまま。
  // それ以外は環境名のフォルダに分け、日次ファイルが混ざらないようにする
  const prefix = APP_ENV === 'production' ? '' : `${APP_ENV}/`;
  return `${prefix}${yyyy}${mm}${dd}-event.${ext}`;
};

const sanitizeDetails = (details: LogDetails): LogDetails => {
  if (!details || typeof details !== 'object') return details;
  const blockKeys = ['password', 'token', 'authorization', 'accessToken', 'idToken', 'refreshToken'];
  const clone = Array.isArray(details) ? [...details] : { ...details };
  Object.keys(clone).forEach((key) => {
    if (blockKeys.includes(String(key))) {
      // 配列・オブジェクトのどちらもキー指定で書き換える（元の実装と同じ）
      (clone as Record<string, unknown>)[key] = '[REDACTED]';
    }
  });
  return clone;
};

const getActor = async (): Promise<LogActor> => {
  try {
    const session = await fetchAuthSession();
    // IDトークンのペイロードは型上 JsonValue のため、参照する項目だけ形を指定する
    const payload = (session?.tokens?.idToken?.payload || {}) as Record<string, unknown>;
    return {
      userId: (payload.sub as string | undefined) || null,
      username: (payload['cognito:username'] as string | undefined) || (payload.email as string | undefined) || null,
      groups: (payload['cognito:groups'] as string[] | undefined) || [],
    };
  } catch (_) {
    return {
      userId: null,
      username: null,
      groups: [],
    };
  }
};

const getEventLabel = (event: string): string => EVENT_LABELS[event] || `イベント:${event}`;

const getLevelLabel = (level?: string): string => {
  switch (String(level || 'INFO').toUpperCase()) {
    case 'ERROR':
      return 'エラー';
    case 'WARN':
      return '警告';
    default:
      return '情報';
  }
};

const safeText = (value: unknown): string => String(value ?? '-').replace(/[\r\n\t]/g, ' ').trim();

const toJstText = (isoString: string): string => {
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return safeText(isoString);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd} ${hh}:${mi}:${ss}`;
  } catch (_) {
    return safeText(isoString);
  }
};

const bodyToString = async (body: unknown): Promise<string> => {
  if (!body) return '';
  // S3のBody・fetchのBody・その他で取り出し方が違うため、生えているメソッドで分岐する
  const candidate = body as {
    transformToString?: () => Promise<string>;
    text?: () => Promise<string>;
  };
  if (typeof candidate.transformToString === 'function') {
    return candidate.transformToString();
  }
  if (typeof candidate.text === 'function') {
    return candidate.text();
  }
  if (typeof Response !== 'undefined') {
    try {
      return await new Response(body as BodyInit).text();
    } catch (_) {
      return '';
    }
  }
  return '';
};

const readExistingDailyLog = async (s3Client: S3Client, key: string): Promise<string> => {
  try {
    const existing = await s3Client.send(new GetObjectCommand({
      Bucket: LOG_BUCKET_NAME,
      Key: key,
    }));

    return await bodyToString(existing.Body);
  } catch (error) {
    const name = asErrorLike(error).name || '';
    const status = asErrorLike(error).$metadata?.httpStatusCode;
    if (name === 'NoSuchKey' || status === 404) {
      return '';
    }
    console.warn('既存日次ログの取得に失敗したため新規作成として継続します:', {
      bucket: LOG_BUCKET_NAME,
      key,
      errorName: name || null,
      httpStatusCode: status || null,
    });
    return '';
  }
};

const flattenDetails = (details: LogDetails): string => {
  if (details === null || details === undefined) return '';
  if (typeof details !== 'object') return String(details);
  const pairs = Object.entries(details).map(([key, value]) => {
    if (value === null || value === undefined) return `${key}=-`;
    if (Array.isArray(value)) return `${key}=${value.join('|')}`;
    if (typeof value === 'object') return `${key}=${JSON.stringify(value)}`;
    return `${key}=${String(value)}`;
  });
  return pairs.join(' ');
};

const toLogLine = (payload: AppLogPayload): string => {
  const actor: Partial<LogActor> = payload.actor || {};
  const groups = Array.isArray(actor.groups) ? actor.groups.join('|') : '';
  const detailsText = safeText(flattenDetails(payload.details));
  const messageText = payload.message ? String(payload.message) : '-';
  const pathText = payload.path || '-';
  const userText = actor.username || actor.userId || 'anonymous';
  const eventLabel = getEventLabel(payload.event);
  const levelLabel = getLevelLabel(payload.level);
  const timestampJst = toJstText(payload.timestamp);
  return [
    `[${timestampJst}]`,
    `レベル:${safeText(levelLabel)}`,
    `イベント:${safeText(eventLabel)}`,
    `(ID:${safeText(payload.event)})`,
    `ユーザー:${safeText(userText)}`,
    `グループ:${safeText(groups || '-')}`,
    `画面:${safeText(pathText)}`,
    `内容:${safeText(messageText)}`,
    `詳細:${detailsText || '-'}`,
  ].join(' | ');
};

// S3 の日次ファイルへ1行追記する（既存の出力先。互換のため形式は変更しない）
const writeToS3 = async (credentials: AwsCredentialIdentity, payload: AppLogPayload): Promise<void> => {
  const useJson = LOG_FORMAT === 'json';
  const key = createDailyLogKey(useJson ? 'json' : 'log');
  const s3Client = new S3Client({ region: REGION, credentials });

  const newEntry = useJson ? `${JSON.stringify(payload)}\n` : `${toLogLine(payload)}\n`;
  const existingBody = await readExistingDailyLog(s3Client, key);
  const body = `${existingBody || ''}${newEntry}`;
  const contentType = useJson ? 'application/json' : 'text/plain; charset=utf-8';

  await s3Client.send(new PutObjectCommand({
    Bucket: LOG_BUCKET_NAME,
    Key: key,
    Body: body,
    ContentType: contentType,
  }));
};

// 同一ストリームへの CreateLogStream 重複呼び出しを避けるためのキャッシュ
const ensuredLogStreams = new Set<string>();

// ログストリームは「環境/日付/ユーザー」単位で分ける。
// 利用者ごとに追える一方、CloudWatch側のストリーム数が無秩序に増えないようにする
const createLogStreamName = (actor: LogActor): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const rawUser = actor.username || actor.userId || 'anonymous';
  // ログストリーム名に使えない文字を落とす（":" と "*" は不可）
  const user = String(rawUser).replace(/[^0-9A-Za-z._-]/g, '_').slice(0, 64);
  // ロググループは環境共通のため、ストリーム名の先頭で環境を分ける
  return `${APP_ENV}/${yyyy}/${mm}/${dd}/${user}`;
};

// CloudWatch Logs へ1件送信する。
// メッセージはJSONにしておき、Logs Insights から actor.username 等で絞り込めるようにする
const writeToCloudWatch = async (credentials: AwsCredentialIdentity, payload: AppLogPayload): Promise<void> => {
  if (!CW_ENABLED) return;

  const logStreamName = createLogStreamName(payload.actor);
  const client = new CloudWatchLogsClient({ region: REGION, credentials });

  if (!ensuredLogStreams.has(logStreamName)) {
    try {
      await client.send(new CreateLogStreamCommand({
        logGroupName: CW_LOG_GROUP_NAME,
        logStreamName,
      }));
    } catch (error) {
      // 既に存在する場合は正常系として扱う
      if (asErrorLike(error).name !== 'ResourceAlreadyExistsException') throw error;
    }
    ensuredLogStreams.add(logStreamName);
  }

  await client.send(new PutLogEventsCommand({
    logGroupName: CW_LOG_GROUP_NAME,
    logStreamName,
    logEvents: [{
      timestamp: Date.now(),
      message: JSON.stringify({ ...payload, eventLabel: getEventLabel(payload.event) }),
    }],
  }));
};

export const writeAppLog = async ({ level = 'INFO', event, message, details }: WriteAppLogParams): Promise<void> => {
  if (!event) return;

  let session: Awaited<ReturnType<typeof fetchAuthSession>> | null = null;
  try {
    session = await fetchAuthSession();
  } catch (error) {
    console.error('ログ送信失敗: 認証セッションを取得できません', {
      event,
      errorName: asErrorLike(error).name || null,
      errorMessage: asErrorLike(error).message || String(error),
    });
    return;
  }

  if (!session?.credentials) {
    console.error('ログ送信失敗: 認証クレデンシャルが取得できません', {
      event,
      bucket: LOG_BUCKET_NAME,
      region: REGION,
    });
    return;
  }

  const actor = await getActor();
  const payload: AppLogPayload = {
    timestamp: new Date().toISOString(),
    app: APP_NAME,
    env: APP_ENV,
    level,
    event,
    message: message || null,
    actor,
    details: sanitizeDetails(details || null),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    path: typeof window !== 'undefined' ? window.location.pathname : null,
  };

  // S3 と CloudWatch は独立して送信する。片方が失敗してももう片方は残す
  const [s3Result, cwResult] = await Promise.allSettled([
    writeToS3(session.credentials, payload),
    writeToCloudWatch(session.credentials, payload),
  ]);

  // ログ送信の失敗で本処理を止めない。原因追跡のためコンソールにのみ出す
  if (cwResult.status === 'rejected') {
    const error = asErrorLike(cwResult.reason);
    console.error('CloudWatchログ送信失敗:', {
      event,
      logGroup: CW_LOG_GROUP_NAME,
      region: REGION,
      errorName: error.name || null,
      errorMessage: error.message || String(cwResult.reason),
      httpStatusCode: error.$metadata?.httpStatusCode || null,
    });
  }

  if (s3Result.status === 'rejected') {
    const error = asErrorLike(s3Result.reason);
    console.error('S3ログ送信失敗:', {
      event,
      bucket: LOG_BUCKET_NAME,
      region: REGION,
      errorName: error.name || null,
      errorMessage: error.message || String(s3Result.reason),
      httpStatusCode: error.$metadata?.httpStatusCode || null,
    });
  }
};

export const logInfo = (event: string, details?: LogDetails, message: string | null = null): Promise<void> => writeAppLog({
  level: 'INFO',
  event,
  details,
  message,
});

export const logWarn = (event: string, details?: LogDetails, message: string | null = null): Promise<void> => writeAppLog({
  level: 'WARN',
  event,
  details,
  message,
});

export const logError = (event: string, details?: LogDetails, message: string | null = null): Promise<void> => writeAppLog({
  level: 'ERROR',
  event,
  details,
  message,
});
